import React from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Text } from '@/shared/ui';
import type { PropertyResponseType } from '@services';
import { translateKnownSchemaItem } from '../../PropertyFormDualPane/schema.constants';
import { SectionCard, EditCardButton, EmptyCardHint } from '../SectionCard';
import type { PropertyDrawerSection } from '../../PropertyDrawer/drawerSections';
import { collectStats } from './characteristics-stat-descriptors';

interface CharacteristicsTabProps {
  property: PropertyResponseType;
  /** Opens the property editor on one section, from a card's pencil. */
  onEditSection?: (section: PropertyDrawerSection) => void;
}

/** One labelled set of amenity chips inside the single amenities card. */
interface AmenityGroup {
  labelKey: string;
  items: string[];
}

/**
 * Collects every amenity list the property carries, in reading order.
 *
 * @param property - The loaded property.
 * @returns The non-empty groups only.
 */
function collectAmenityGroups(property: PropertyResponseType): AmenityGroup[] {
  const groups: AmenityGroup[] = [
    { labelKey: 'detailView.characteristics.highlights', items: property.description?.highlights ?? [] },
    { labelKey: 'detailView.characteristics.infrastructure', items: property.amenities?.infrastructure ?? [] },
    { labelKey: 'detailView.characteristics.leisureAreas', items: property.amenities?.external ?? [] },
    { labelKey: 'detailView.characteristics.facilities', items: property.amenities?.internal ?? [] },
    { labelKey: 'detailView.characteristics.security', items: property.amenities?.security ?? [] },
    { labelKey: 'detailView.characteristics.accessibility', items: property.amenities?.accessibility ?? [] },
    { labelKey: 'detailView.characteristics.condoLeisure', items: property.condominium?.leisure_areas ?? [] },
    { labelKey: 'detailView.characteristics.condoSecurity', items: property.condominium?.security ?? [] },
    { labelKey: 'detailView.characteristics.condoExpenses', items: property.condominium?.included_expenses ?? [] },
  ];
  return groups.filter((group) => group.items.length > 0);
}

/**
 * Renders amenity values as localized chips.
 */
const ChipList: React.FC<{ items: string[]; t: TFunction }> = ({ items, t }) => (
  <div className="flex flex-wrap gap-[var(--space-1-5)]">
    {items.map((item) => (
      <Text
        component="span"
        key={item}
        className="rounded-(--radius-md) border border-[color-mix(in_srgb,var(--seed-base)_15%,transparent)] bg-[color-mix(in_srgb,var(--seed-base)_6%,var(--surface-primary))] px-[var(--space-2-5)] py-[var(--space-0-5)] text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-[var(--text-primary)]"
      >
        {translateKnownSchemaItem(t, item)}
      </Text>
    ))}
  </div>
);

/**
 * The numeric facts, as a grid of labelled tiles.
 */
const StatGrid: React.FC<{ stats: ReturnType<typeof collectStats> }> = ({ stats }) => (
  <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,150px),1fr))] gap-[var(--space-3)]">
    {stats.map((item) => (
      <div
        key={item.label}
        className="flex min-w-0 items-center gap-[var(--space-2)] rounded-(--radius-md) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-2)]"
      >
        <Text component="span" className="flex flex-none text-[var(--seed-base)]">{item.icon}</Text>
        <div className="flex min-w-0 flex-col">
          <Text
            component="span"
            className="truncate text-[length:var(--text-meta)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
          >
            {item.label}
          </Text>
          <Text
            component="span"
            className="text-[length:var(--text-body-sm)] [font-weight:var(--font-semibold)] text-[var(--text-primary)]"
          >
            {item.value}
          </Text>
        </div>
      </div>
    ))}
  </div>
);

/**
 * Renders the characteristics tab: the numeric stats grid, then every amenity
 * list in a single card.
 *
 * The seven amenity lists used to be seven stacked cards, each with its own
 * border, uppercase heading and rule, wrapped around four or five words. That
 * was a screen of scrolling for a page of chips. One card with the groups as
 * small labels in a two- or three-column grid says the same thing in a
 * glance, and the header count answers "how well equipped is this" before the
 * reader parses a single chip.
 *
 * The amenities card stays visible while empty, so the lists can be filled in
 * from its pencil.
 *
 * @param props.property - The loaded property.
 * @param props.onEditSection - Opens the editor on the amenities section.
 * @returns The rendered tab.
 */
export const CharacteristicsTab: React.FC<CharacteristicsTabProps> = ({ property, onEditSection }) => {
  const { t } = useTranslation('features/properties');
  const stats = collectStats(property, t);
  const groups = collectAmenityGroups(property);
  const amenityCount = groups.reduce((total, group) => total + group.items.length, 0);

  return (
    <div className="flex flex-col gap-[var(--space-4)]">
      {stats.length > 0 && (
        <SectionCard title={t('detailView.overview.mainCharacteristics')}>
          <StatGrid stats={stats} />
        </SectionCard>
      )}

      <SectionCard
        title={t('detailView.characteristics.amenitiesTitle')}
        data-testid="property-amenities-card"
        titleAccessory={
          <Text component="span" className="flex items-center gap-[var(--space-2)]">
            {groups.length > 0 ? (
              <Text
                component="span"
                className="whitespace-nowrap text-[length:var(--text-meta)] text-[var(--text-tertiary-soft)]"
              >
                {t('detailView.characteristics.amenityCount', { count: amenityCount })}
              </Text>
            ) : null}
            {onEditSection ? (
              <EditCardButton onClick={() => onEditSection('amenities')} data-testid="property-amenities-edit" />
            ) : null}
          </Text>
        }
      >
        {groups.length === 0 ? <EmptyCardHint /> : null}
        {groups.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,230px),1fr))] gap-x-[var(--space-5)] gap-y-[var(--space-4)]">
            {groups.map((group) => (
              <div key={group.labelKey} className="flex min-w-0 flex-col gap-[var(--space-2)]">
                <Text
                  component="span"
                  className="text-[length:var(--text-meta)] [font-weight:var(--font-semibold)] leading-[var(--leading-snug)] text-[var(--text-tertiary)]"
                >
                  {t(group.labelKey)}
                </Text>
                <ChipList items={group.items} t={t} />
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
};
